import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import AppShell from "@/components/AppShell";
import Landing, { LegalPage } from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import { ForgotPassword, ChangePassword } from "@/pages/PasswordPages";
import Dashboard from "@/pages/Dashboard";
import Tracker from "@/pages/Tracker";
import Documents, { DocumentEditor } from "@/pages/Documents";
import Evidence, { GapAnalysis } from "@/pages/Evidence";
import { LabProfile, Team, Settings, Reports, Reference } from "@/pages/Workspace";
import { AdminDashboard, AdminLab } from "@/pages/Admin";
import { modulePage } from "@/pages/Modules";
import Billing from "@/pages/Billing";
import DevPortal from "@/pages/DevPortal";
import { AdminBilling } from "@/pages/AdminBilling";

const HOME = { super_admin: "/admin", developer: "/dev" };
const home = (u) => HOME[u.role] || "/dashboard";

const Spin = () => <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#48217A]" /></div>;

function Guard({ admin, dev, children, allowChange }) {
  const { user } = useAuth();
  if (user === null) return <Spin />;
  if (!user) return <Navigate to={dev ? "/qlog" : admin ? "/admin/login" : "/login"} replace />;
  if (user.must_change_password && !allowChange) return <Navigate to="/change-password" replace />;
  if (allowChange) return children;
  if (dev && user.role !== "developer") return <Navigate to={home(user)} replace />;
  if (admin && user.role !== "super_admin") return <Navigate to={home(user)} replace />;
  if (!admin && !dev && HOME[user.role]) return <Navigate to={home(user)} replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <Spin />;
  if (user) return <Navigate to={user.must_change_password ? "/change-password" : HOME[user.role] ? home(user) : sessionStorage.getItem("ic_onboard") ? "/profile" : "/dashboard"} replace />;
  return children;
}

const MODS = ["audits", "capa", "equipment", "personnel", "risks", "indicators", "reviews", "tasks"];
const ModPages = Object.fromEntries(MODS.map((m) => [m, modulePage(m)]));

function App() {
  return (
    <div className="App">
      <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<PublicOnly><Landing /></PublicOnly>} />
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/signup" element={<PublicOnly><Signup /></PublicOnly>} />
            <Route path="/admin/login" element={<PublicOnly><Login admin /></PublicOnly>} />
            <Route path="/qlog" element={<PublicOnly><Login dev /></PublicOnly>} />
            <Route path="/dev" element={<Guard dev><DevPortal /></Guard>} />
            <Route path="/admin/billing" element={<Guard admin><AdminBilling /></Guard>} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/qlog/reset" element={<ForgotPassword dev />} />
            <Route path="/legal/:kind" element={<LegalPage />} />
            <Route path="/change-password" element={<Guard allowChange><ChangePassword /></Guard>} />
            <Route path="/admin" element={<Guard admin><AdminDashboard /></Guard>} />
            <Route path="/admin/labs/:id" element={<Guard admin><AdminLab /></Guard>} />
            <Route element={<Guard><AppShell /></Guard>}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/tracker" element={<Tracker />} />
              <Route path="/gap-analysis" element={<GapAnalysis />} />
              <Route path="/reference" element={<Reference />} />
              <Route path="/documents" element={<Documents />} />
              <Route path="/documents/:id" element={<DocumentEditor />} />
              <Route path="/evidence" element={<Evidence />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/profile" element={<LabProfile />} />
              <Route path="/team" element={<Team />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/billing" element={<Billing />} />
              {MODS.map((m) => { const P = ModPages[m]; return <Route key={m} path={`/${m}`} element={<P />} />; })}
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
